import { faFolderOpen } from "@fortawesome/free-regular-svg-icons";
import {
  faBlog,
  faCalendarDays,
  faChevronUp,
  faClockRotateLeft,
  faClose,
  faCookie,
  faCookieBite,
  faFolder,
  faHeart,
  faLaptopCode,
  faNavicon,
  faPenNib,
  faPersonRays,
  faRainbow,
  faSearch,
  faTag,
  faTags,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { Link, navigate, PageProps } from "gatsby";
import { throttle } from "lodash-es";
import * as React from "react";
import RichPresence, { type RichPresenceActivity } from "rich-presence-react";

import ActionButton from "../components/action-button";
import AlgoliaSearch from "../components/algolia-search";
import CircularText from "../components/circular-text";
import GitalkComponent from "../components/gitalk";
import Icon from "../components/icon";
import Planets from "../components/planets";
import Post from "../components/post";
import RotatingText from "../components/rotating-text";
import ThemeSwitcher from "../components/theme-switcher";
import { MIIT_BEIAN_LABEL, MPS_BEIAN_CODE } from "../constants/beian";
import { FOOTER_SOCIAL_ITEMS, NAVBAR_ITEMS } from "../constants/navbar";
import useAllMdx from "../hooks/useAllMdx";
import useDiscordActivity from "../hooks/useDiscordActivity";
import useTailwindBreakpoint from "../hooks/useScreenBreakpoint";
import useSiteMetadata from "../hooks/useSiteMetadata";
import useTheme from "../hooks/useTheme";
import Navbar from "./navbar";
import SiderBar, { SiderBarProps } from "./sider-bar";

type SubNavbarActiveKey = "nav" | "posts" | "toc";

type PostSortKey = "date" | "updated";

type TocItemState = "past" | "active" | "none";

interface TocItemProps {
  heading: HTMLHeadingElement;
  index: number;
  state: TocItemState;
  onRef: (el: HTMLLIElement | null, index: number) => void;
}

const TocItem = React.memo<TocItemProps>(({ heading, index, state, onRef }) => {
  const marginLeft = `${(Number(heading.nodeName[1]) - 2) * 1}rem`;
  return (
    <li
      ref={(el) => onRef(el, index)}
      style={{ marginLeft }}
      className={`
        mb-1 item-selectable rounded-lg
        ${
          state === "past"
            ? "text-foreground-secondary"
            : state === "active"
              ? "item-selected"
              : ""
        }
      `}
    >
      <a href={`#${heading.id}`} className="block px-3 py-2 transition">
        <span
          dangerouslySetInnerHTML={{ __html: heading.innerHTML }}
          className="pointer-events-none"
        />
      </a>
    </li>
  );
});

interface SidebarStaticContentProps {
  activities: RichPresenceActivity[];
  planetSize: number;
}

const SidebarStaticContent = React.memo<SidebarStaticContentProps>(
  ({ activities, planetSize }) => {
    const { resolvedTheme } = useTheme();
    return (
      <>
        <div className={`relative z-10 flex flex-col gap-3`}>
          {activities.map((activity, index) => (
            <RichPresence
              className="
                opacity-80 transition-opacity
                hover:opacity-100
              "
              key={index}
              activity={activity}
              theme={resolvedTheme ?? "dark"}
              size="normal"
            />
          ))}
        </div>
        <div
          className={`
            pointer-events-none fixed bottom-0 hidden overflow-hidden opacity-40
            sm:block
          `}
          style={{ height: planetSize * 0.82, left: -planetSize * 0.18 }}
        >
          <Planets size={planetSize} />
        </div>
      </>
    );
  },
);

const Layout: React.FC<PageProps> = (props) => {
  const { children, location } = props;
  const { href, pathname, search, hash } = location;
  // Gatsby can pass a wildcard route path during SSR; use the actual URL.
  const path = pathname;

  const [maskRemoved, setMaskRemoved] = React.useState<boolean>(false);
  const [subNavbarActiveKey, setSubNavbarActiveKey] =
    React.useState<SubNavbarActiveKey>();
  const [postSortKey, setPostSortKey] = React.useState<PostSortKey>("date");
  /** 小屏幕开启导航栏抽屉状态 */
  const [openSubNavbarDrawer, setOpenSubNavbarDrawer] =
    React.useState<boolean>(false);
  const [isImmersive, setIsImmersive] = React.useState<boolean>(false);
  const [pageTitle, setPageTitle] = React.useState<React.ReactNode>("");
  const [openAlgoliaSearch, setOpenAlgoliaSearch] =
    React.useState<boolean>(false);
  const [pageHeadings, setPageHeadings] = React.useState<HTMLHeadingElement[]>(
    [],
  );
  const [currentHeading, setCurrentHeading] = React.useState<number>(-1);
  const [showBackTop, setShowBackTop] = React.useState<boolean>(false);
  const [readProgress, setReadProgress] = React.useState<number>(0); // %
  const [printDate, setPrintDate] = React.useState<string>("");
  const [currentYear, setCurrentYear] = React.useState<number | null>(null);
  const [printHref, setPrintHref] = React.useState<string>("");

  const hashRef = React.useRef<string>("");
  const mainRef = React.useRef<HTMLElement>(null);
  const pageRef = React.useRef<HTMLDivElement>(null);
  const tocRefs = React.useRef<HTMLLIElement[]>([]);
  const savedScrollTopRef = React.useRef<Record<string, number>>({});
  const pageHeadingsRef = React.useRef<HTMLHeadingElement[]>([]);
  const onScrolledRef = React.useRef<() => void>(() => {});

  const tocRefCallback = React.useCallback(
    (el: HTMLLIElement | null, index: number) => {
      tocRefs.current[index] = el as HTMLLIElement;
    },
    [],
  );

  const { title: siteTitle, owner: siteOwner } = useSiteMetadata();
  const posts = useAllMdx();
  const sortedPosts = React.useMemo(
    () =>
      [...posts].sort(
        (a, b) =>
          dayjs(b.frontmatter[postSortKey]).valueOf() -
          dayjs(a.frontmatter[postSortKey]).valueOf(),
      ),
    [posts, postSortKey],
  );
  const postSortDescription =
    postSortKey === "date"
      ? "按发布时间从新到旧排序，点击切换为更新时间"
      : "按更新时间从新到旧排序，点击切换为发布时间";
  const [breakpoint, layoutInitialized] = useTailwindBreakpoint();
  const { activities } = useDiscordActivity();

  /** 当前路由是否为博客页 */
  const [isPostPage, postSlug] = React.useMemo(() => {
    if (/^\/about-me\//.test(path)) {
      return [true, "about-me"];
    }

    const result = path.match(/^\/posts\/(.+)\//);
    return [result !== null, result?.[1]];
  }, [path]);

  //#region 沉浸模式能力
  React.useEffect(() => {
    React.startTransition(() => {
      setIsImmersive(
        new URLSearchParams(window.location.search).has("immersive"),
      );
    });
  }, []);

  /** 是否激活了沉浸模式 */
  const isImmersiveActivated = React.useMemo(() => {
    return isImmersive && isPostPage && breakpoint["lg"];
  }, [breakpoint, isImmersive, isPostPage]);

  /** 监听沉浸模式 URL Search Params */
  React.useEffect(() => {
    if (!layoutInitialized) return;

    const params = new URLSearchParams(search);
    if (isImmersiveActivated) params.set("immersive", "true");
    else params.delete("immersive");
    const newSearch = params.toString() ? `?${params.toString()}` : "";
    if (newSearch === search) return;

    navigate(`${pathname}${newSearch}${hash || ""}`, {
      replace: true,
    });
  }, [isImmersiveActivated, layoutInitialized, pathname, search, hash]);
  //#endregion

  //#region 切换路由时初始化页面状态
  React.useEffect(() => {
    React.startTransition(() => {
      setPageTitle("");
      setOpenSubNavbarDrawer(false);
    });
    tocRefs.current = [];
    mainRef.current?.scrollTo({
      top: savedScrollTopRef.current?.[path] ?? 0,
      behavior: "instant",
    });

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      savedScrollTopRef.current[path] = mainRef.current?.scrollTop ?? 0;
    };
  }, [path]);
  //#endregion

  //#region 自动设置侧边栏激活的页面
  /** 依据路由与断点计算出的目标激活标签 */
  const desiredSubNavbarActiveKey: SubNavbarActiveKey = isPostPage
    ? "toc"
    : breakpoint["2xl"]
      ? "posts"
      : "nav";

  // 路由/断点变化时在渲染阶段同步纠正激活标签：subSiderBarItems 会在同一次渲染中
  // 同步剔除/加入 toc 项，若改用 useEffect 异步纠正，会出现提交后
  // "激活标签在可用列表中找不到" 的中间帧，导致内容区短暂空白
  const [prevSubNavbarDeps, setPrevSubNavbarDeps] = React.useState<{
    isPostPage: boolean;
    is2xl: boolean;
  } | null>(null);
  if (
    prevSubNavbarDeps === null ||
    prevSubNavbarDeps.isPostPage !== isPostPage ||
    prevSubNavbarDeps.is2xl !== breakpoint["2xl"]
  ) {
    setPrevSubNavbarDeps({ isPostPage, is2xl: breakpoint["2xl"] });
    setSubNavbarActiveKey(desiredSubNavbarActiveKey);
  }
  //#endregion

  //#region 更新博客中 Headings 距离顶端的距离，适配图片加载完成等导致距离变化的情况
  React.useEffect(() => {
    const pageDom = pageRef.current;
    if (isPostPage && pageDom) {
      const updateHeadings = () => {
        const headings: HTMLHeadingElement[] = Array.from(
          pageDom.querySelectorAll("h1, h2, h3, h4, h5, h6"),
        );
        headings.forEach((heading) => {
          heading.id = encodeURIComponent(heading.innerText);
        });
        if (headings.length) {
          setPageHeadings(
            headings.filter((heading) => heading.nodeName !== "H1"),
          );
        } else {
          setPageHeadings([]);
        }
      };

      // ResizeObserver 只在容器尺寸变化时触发，客户端路由切换时新页面内容
      // 可能异步挂载且不改变（受 min-height 限制的）容器尺寸，因此需要额外用
      // MutationObserver 监听内容本身的挂载/替换，避免目录停留在空状态
      const resizeObserver = new ResizeObserver(updateHeadings);
      const mutationObserver = new MutationObserver(updateHeadings);

      resizeObserver.observe(pageDom);
      mutationObserver.observe(pageDom, { childList: true, subtree: true });
      updateHeadings();

      return () => {
        resizeObserver.unobserve(pageDom);
        mutationObserver.disconnect();
      };
    } else {
      setPageHeadings([]);
    }
  }, [path, isPostPage]);
  //#endregion

  //#region 处理 Hash 查询，跳转到指定 Heading
  React.useEffect(() => {
    if (hash && hashRef.current !== hash) {
      const targetId = hash.slice(1);
      const pageHeading = pageHeadings.find(
        (heading) => heading.id === targetId,
      );

      if (pageHeading) {
        pageHeading.scrollIntoView();
        hashRef.current = hash;
      }
    }
  }, [hash, pageHeadings]);
  //#endregion

  //#region 同步 pageHeadings 到 ref，避免滚动监听因目录更新而重复挂载
  React.useEffect(() => {
    pageHeadingsRef.current = pageHeadings;
    onScrolledRef.current();
  }, [pageHeadings]);
  //#endregion

  //#region 监听博客滚动，更新目录列表与阅读进度
  React.useEffect(() => {
    const mainDom = mainRef.current;
    const pageDom = pageRef.current;
    if (isPostPage && mainDom && pageDom) {
      const articleDom = pageDom.querySelector<HTMLElement>("article");
      const onScrolled = () => {
        const headings = pageHeadingsRef.current;
        const scrollTop = mainDom.scrollTop;
        for (let index = headings.length - 1; index >= 0; index -= 1) {
          const { offsetTop } = headings[index];
          if (Math.ceil(scrollTop) >= offsetTop) {
            setCurrentHeading(index);
            tocRefs.current[index]?.scrollIntoView();
            break;
          }
          if (index === 0) {
            setCurrentHeading(-1);
            tocRefs.current[0]?.scrollIntoView();
          }
        }

        if (articleDom) {
          const mainRect = mainDom.getBoundingClientRect();
          const articleRect = articleDom.getBoundingClientRect();
          const articleTopInScroll = articleRect.top - mainRect.top + scrollTop;
          const articleHeight = articleDom.clientHeight;
          const headerEl = mainDom.querySelector("header");
          const headerHeight = headerEl?.clientHeight ?? 0;
          const visibleBottom = scrollTop + mainDom.clientHeight;
          const readStart = articleTopInScroll + headerHeight;
          const readEnd = articleTopInScroll + articleHeight;
          const progress = Math.floor(
            ((visibleBottom - readStart) / (readEnd - readStart)) * 100,
          );
          setReadProgress(progress);
        }
      };
      onScrolledRef.current = onScrolled;
      React.startTransition(() => onScrolled());
      const throttleOnScrolled = throttle(onScrolled, 50);
      mainDom.addEventListener("scroll", throttleOnScrolled);
      return () => mainDom.removeEventListener("scroll", throttleOnScrolled);
    } else {
      React.startTransition(() => {
        setCurrentHeading(-1);
        setReadProgress(0);
      });
    }
  }, [path, isPostPage]);
  //#endregion

  //#region 非博客页标题：派生自路由，无需等待副作用触发，避免路由切换时标题闪烁
  const staticPageTitle = React.useMemo<React.ReactNode>(() => {
    if (isPostPage) return null;

    let match: RegExpMatchArray | null;
    if (path === "/") {
      return (
        <>
          <Icon icon={faPenNib} className="p-2" />
          <span>所有博客</span>
        </>
      );
    } else if (/^\/works\/?$/.test(path)) {
      return (
        <>
          <Icon icon={faLaptopCode} className="p-2" />
          <span>作品集</span>
        </>
      );
    } else if (/^\/friends\/?$/.test(path)) {
      return (
        <>
          <Icon icon={faHeart} className="p-2" />
          <span>朋友们</span>
        </>
      );
    } else if (/^\/tags\/?$/.test(path)) {
      return (
        <>
          <Icon icon={faTags} className="p-2" />
          <span>所有标签</span>
        </>
      );
    } else if ((match = path.match(/^\/tags\/(.+?)\/?$/))) {
      return (
        <>
          <Icon icon={faTag} className="p-2" />
          <span>{decodeURIComponent(match[1])}</span>
        </>
      );
    } else if (/^\/categories\/?$/.test(path)) {
      return (
        <>
          <Icon icon={faFolder} className="p-2" />
          <span>所有分类</span>
        </>
      );
    } else if ((match = path.match(/^\/categories\/(.+?)\/?$/))) {
      return (
        <>
          <Icon icon={faFolderOpen} className="p-2" />
          <span>{decodeURIComponent(match[1])}</span>
        </>
      );
    } else if (/^\/404\/?$/.test(path)) {
      return (
        <>
          <Icon icon={faWarning} className="p-2" />
          <span>未开放区域</span>
        </>
      );
    }
    return null;
  }, [isPostPage, path]);
  //#endregion

  //#region 博客页标题
  React.useEffect(() => {
    if (!isPostPage) return;

    const pageDom = pageRef.current;
    if (!pageDom) return;

    const postTitle = pageDom.querySelector("h1");
    if (!postTitle) {
      React.startTransition(() => setShowBackTop(true));
      return;
    }

    // 监听博客页面滚动，更新页面标题为博客标题
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setPageTitle(
          postSlug === "about-me" ? (
            <>
              <Icon icon={faPersonRays} className="p-2" />
              <span>关于我</span>
            </>
          ) : (
            <>
              <Icon icon={faBlog} className="p-2" />
              <span>博客</span>
            </>
          ),
        );
        setShowBackTop(false);
      } else {
        setPageTitle(
          <span className="line-clamp-1" title={postTitle.innerText}>
            {postTitle.innerText}
          </span>,
        );
        setShowBackTop(true);
      }
    });

    observer.observe(postTitle);
    return () => {
      observer.unobserve(postTitle);
    };
  }, [isPostPage, path, postSlug, layoutInitialized]);
  //#endregion

  //#region 挂载后更新时间相关的客户端状态
  React.useEffect(() => {
    React.startTransition(() => {
      setPrintDate(dayjs().toISOString());
      setCurrentYear(dayjs().year());
    });
  }, []);

  React.useEffect(() => {
    React.startTransition(() => {
      setPrintHref(href ?? "");
    });
  }, [href]);
  //#endregion

  //#region 侧边栏
  /** 可切换侧边栏组件列表 */
  const subSiderBarItems: NonNullable<
    SiderBarProps<SubNavbarActiveKey>["items"]
  > = [];

  /** 星球图标大小 */
  const planetSize = breakpoint["2xl"]
    ? 260
    : breakpoint["lg"]
      ? 276
      : breakpoint["sm"]
        ? 292
        : 0;

  /** 侧边栏导航组件 */
  const siderBarNavItem = React.useMemo(
    () => (
      <>
        <Navbar items={NAVBAR_ITEMS} activeKey={path} />
        <SidebarStaticContent activities={activities} planetSize={planetSize} />
      </>
    ),
    [path, activities, planetSize],
  );
  if (!breakpoint["2xl"]) {
    subSiderBarItems.push({
      key: "nav",
      label: "导航",
      children: siderBarNavItem,
    });
  }

  /** 侧边栏博客列表组件 */
  const siderBarPostItem = React.useMemo(
    () => (
      <ol>
        {sortedPosts.map((post) => {
          const isActive = new RegExp(`^/posts/${post.fields.slug}/`).test(
            path,
          );

          return (
            <li
              key={post.fields.slug}
              className={`
                rounded-lg transition-all
                ${isActive ? "sticky bottom-0 bg-background" : ""}
              `}
            >
              <Post
                post={post}
                dateField={postSortKey}
                className={`
                  mb-2
                  ${isActive ? "item-selected" : ""}
                `}
                excerptClassName="hidden"
              />
            </li>
          );
        })}
      </ol>
    ),
    [sortedPosts, path, postSortKey],
  );
  subSiderBarItems.push({
    key: "posts",
    label: "博客列表",
    children: siderBarPostItem,
  });

  /** 侧边栏博客目录组件 */
  const siderBarTocItem = React.useMemo(
    () => (
      <ol className={`px-3`}>
        {pageHeadings.map((heading, index) => (
          <TocItem
            key={heading.innerText}
            heading={heading}
            index={index}
            state={
              currentHeading > index
                ? "past"
                : currentHeading === index
                  ? "active"
                  : "none"
            }
            onRef={tocRefCallback}
          />
        ))}
      </ol>
    ),
    [pageHeadings, currentHeading, tocRefCallback],
  );

  if (isPostPage) {
    subSiderBarItems.push({
      key: "toc",
      label: "目录",
      children: siderBarTocItem,
    });
  }
  //#endregion

  return (
    <div
      className="
        flex h-screen
        print:h-auto
      "
    >
      {/* 初始化完成前的蒙版层 */}
      {!maskRemoved && (
        <div
          className={`
            absolute inset-0 z-50 overflow-hidden bg-background transition
            duration-500
            ${layoutInitialized ? "pointer-events-none opacity-0" : ""}
          `}
          onTransitionEnd={() => layoutInitialized && setMaskRemoved(true)}
        >
          <div
            className="
              absolute right-4 bottom-4
              md:right-6 md:bottom-6
              xl:right-8 xl:bottom-8
            "
          >
            <CircularText
              text="NOW*LOADING*NOW*LOADING*"
              onHover="goBonkers"
              spinDuration={20}
            />
          </div>
        </div>
      )}

      {/* 侧边栏 */}
      <SiderBar
        className={`
          hidden w-72
          2xl:block
          print:hidden
          ${isImmersiveActivated ? "hidden!" : ""}
        `}
        header={
          <div className="mx-5 px-4">
            <Link
              to="/"
              className="
                flex items-center text-lg font-bold transition-colors
                hover:text-primary
              "
            >
              Lolipop's
              <RotatingText
                texts={["Studio", "Space", "Homepage", "Blog"]}
                mainClassName="ml-1.5 overflow-hidden justify-center"
                staggerFrom="last"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-120%" }}
                staggerDuration={0.025}
                splitLevelClassName="overflow-hidden"
                transition={{ type: "spring", damping: 30, stiffness: 400 }}
                rotationInterval={10000}
                splitBy="characters"
                auto
                loop
              />
            </Link>
          </div>
        }
        bodyClassName="px-4 relative"
      >
        {siderBarNavItem}
      </SiderBar>
      <SiderBar<SubNavbarActiveKey>
        items={subSiderBarItems}
        activeKey={subNavbarActiveKey}
        onActiveKeyChange={setSubNavbarActiveKey}
        headerExtra={
          subNavbarActiveKey === "posts" && (
            <button
              type="button"
              aria-label={postSortDescription}
              title={postSortDescription}
              onClick={() =>
                setPostSortKey((key) => (key === "date" ? "updated" : "date"))
              }
              className="
                flex size-8 item-selectable items-center justify-center
                rounded-md
                focus-visible:outline-2 focus-visible:outline-offset-2
                focus-visible:outline-primary
              "
            >
              <Icon
                icon={
                  postSortKey === "date" ? faCalendarDays : faClockRotateLeft
                }
                aria-hidden="true"
              />
            </button>
          )
        }
        headerClassName="px-4 mx-3"
        bodyClassName="px-4 h-[calc(100vh-var(--height-header)*2)] lg:h-[calc(100vh-var(--height-header))]"
        className={`
          print:hidden
          ${isImmersiveActivated ? "hidden!" : ""}
          ${
            breakpoint["lg"]
              ? `
                w-96
                2xl:w-88
              `
              : `
                fixed top-[calc(var(--height-header))] z-20
                h-[calc(100vh-var(--height-header))] w-full border-none
                transition
                sm:w-96
                ${openSubNavbarDrawer ? "translate-x-0 opacity-100" : "pointer-events-none -translate-x-96 opacity-0"}`
          }
        `}
      />

      {/* 路由主体 */}
      <main
        ref={mainRef}
        className={`
          flex-1 bg-background
          ${openSubNavbarDrawer || openAlgoliaSearch ? "overflow-hidden" : "overflow-auto"}`}
      >
        <header
          className={`
            sticky top-0 z-20 flex h-header items-center bg-background-light
            px-8 backdrop-blur-xs
            lg:bg-header-background
            print:hidden
            ${isImmersiveActivated ? "hidden!" : ""}
          `}
        >
          <div
            className={`
              mr-4 flex size-8 item-selectable items-center justify-center
              rounded-md border-2 border-foreground
              lg:hidden
              ${
                openSubNavbarDrawer
                  ? `
                    bg-foreground text-background
                    hover:border-foreground-secondary
                    hover:bg-foreground-secondary hover:text-background-darker
                  `
                  : ""
              }
            `}
            onClick={() => {
              setOpenSubNavbarDrawer((prev) => !prev);
            }}
          >
            <Icon
              icon={openSubNavbarDrawer ? faClose : faNavicon}
              className="size-4"
            />
          </div>
          <div className="line-clamp-1 flex flex-1 items-center text-lg font-bold">
            {isPostPage ? pageTitle : staticPageTitle}
          </div>
          <div
            className="
              ml-auto flex shrink-0 items-center justify-end gap-2 pl-4
              sm:pl-8
            "
          >
            <ThemeSwitcher />
            <Icon
              icon={faSearch}
              className="item-selectable rounded-md p-2"
              onClick={() => setOpenAlgoliaSearch(true)}
            />
          </div>
        </header>

        <div
          ref={pageRef}
          className="
            relative
            min-h-[calc(100vh-var(--height-header)-var(--height-footer))] p-8
            lg:px-16 lg:py-12
            2xl:px-24
          "
        >
          {/* 站点信息（打印时显示） */}
          <div
            className="
              mb-12 hidden
              print:block
            "
          >
            <div>Site: {siteTitle}</div>
            <div>Print Date: {printDate}</div>
            <div>
              Link:{" "}
              <a
                href={printHref}
                target="_blank"
                rel="noreferrer"
                className="item-link"
              >
                {printHref}
              </a>
            </div>
          </div>

          {layoutInitialized && children}

          {/* 博客页的评论系统 */}
          {isPostPage && !!postSlug && (
            <GitalkComponent
              key={postSlug}
              gitalkId={postSlug}
              className="
                mx-auto mt-24 max-w-xl
                print:hidden
              "
            />
          )}

          {/* 博客页的操作按钮 */}
          {isPostPage && (
            <div
              className="
                sticky bottom-8 z-10 mt-12 flex flex-col items-end justify-end
                gap-4 text-sm
                print:hidden
              "
            >
              {/* 回到顶部按钮 */}
              {!isImmersiveActivated && (
                <div
                  className={`
                    relative flex size-9 items-center justify-center
                    rounded-full transition
                    ${showBackTop ? "opacity-100" : "pointer-events-none opacity-0"}`}
                  style={{
                    background: `conic-gradient(var(--foreground) ${readProgress * 3.6}deg, var(--foreground-tertiary) ${readProgress * 3.6}deg)`,
                  }}
                >
                  <ActionButton
                    icon={faChevronUp}
                    onClick={() =>
                      mainRef.current?.scrollTo({ top: 0, behavior: "smooth" })
                    }
                  />
                </div>
              )}
              {/* 沉浸式浏览按钮 */}
              <div
                className="
                  hidden
                  lg:block
                "
              >
                <ActionButton
                  icon={isImmersive ? faCookieBite : faCookie}
                  onClick={() => setIsImmersive((prev) => !prev)}
                  className={`
                    size-9 border-2
                    ${isImmersive ? "border-foreground" : "border-foreground-tertiary"}`}
                  title={isImmersive ? "退出沉浸模式" : "进入沉浸模式"}
                />
              </div>
            </div>
          )}
        </div>

        <footer
          className={`
            flex h-footer flex-col justify-center gap-2 border-t
            border-foreground-tertiary bg-background-lighter px-16
            print:hidden
            ${isImmersiveActivated ? "hidden!" : ""}
          `}
        >
          <div
            className="
              flex flex-col gap-2
              md:flex-row md:items-center
            "
          >
            <span>
              Powered by{" "}
              <a
                href="https://www.gatsbyjs.com/"
                target="_blank"
                rel="noreferrer"
                className="item-link"
              >
                Gatsby
              </a>
            </span>
            <Icon
              icon={faRainbow}
              className="
                hidden
                md:block
              "
            />
            <span>
              Inspired by{" "}
              <a
                href="https://thesis.priority.vision/"
                target="_blank"
                rel="noreferrer"
                className="item-link"
              >
                Thesis
              </a>
            </span>
          </div>
          {(MIIT_BEIAN_LABEL || MPS_BEIAN_CODE) && (
            <div
              className="
                flex flex-col gap-2
                md:flex-row md:items-center
              "
            >
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noreferrer"
              >
                {MIIT_BEIAN_LABEL}
              </a>
              <img
                src="/images/beian.png"
                alt="beian-icon"
                className="
                  hidden size-5
                  md:block
                "
              />
              <a
                href={`http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=${MPS_BEIAN_CODE}`}
                target="_blank"
                rel="noreferrer"
              >
                京公网安备 {MPS_BEIAN_CODE}号
              </a>
            </div>
          )}
          <div className="my-3 w-full border border-foreground-tertiary" />
          <div className="flex h-6 items-center justify-between">
            <span className="text-foreground-secondary">
              © {currentYear ?? ""} {siteOwner}
            </span>
            <div className="-mr-2 flex gap-2 text-xl">
              {FOOTER_SOCIAL_ITEMS.map((item) => (
                <a
                  key={item.url}
                  title={item.label}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="leading-none"
                >
                  <Icon
                    icon={item.icon}
                    className="item-selectable rounded-md p-2"
                  />
                </a>
              ))}
            </div>
          </div>
        </footer>

        {/* 小屏幕：打开侧边栏抽屉时的蒙版层 */}
        <div
          className={`
            ${openSubNavbarDrawer ? "block" : "hidden"}
            absolute inset-0 bg-overlay backdrop-blur-xs
            lg:hidden
            print:hidden
          `}
          onClick={() => setOpenSubNavbarDrawer(false)}
        />
      </main>

      {/* Algolia 搜索窗口 */}
      {openAlgoliaSearch && (
        <div
          onClick={() => setOpenAlgoliaSearch(false)}
          className="
            absolute inset-0 z-30 bg-overlay backdrop-blur-xs
            print:hidden
          "
        >
          <AlgoliaSearch
            onClose={() => setOpenAlgoliaSearch(false)}
            className="
              mx-auto mt-16 max-h-[calc(100vh-4rem)] border
              border-foreground-tertiary bg-dialog-background
            "
          />
        </div>
      )}
    </div>
  );
};

export default Layout;
